import React from "react";
import { Coins, Sparkles, Loader2 } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";
import { formatPrice } from "@/utils/price";
import { CHECKOUT } from "@/constants/testIds";

/**
 * Reusable token pack picker. Shown whenever the user doesn't have enough tokens to pay
 * for something (an order, a download unlock...). Highlights the smallest pack that covers
 * the missing amount. Calling onBuy(pack) is expected to purchase + auto-resume the action.
 */
export default function TokenPackPicker({ packs, missingTokens, onBuy, loadingPack }) {
  const { language } = useI18n();
  const recommendedPack = packs.find((p) => p >= missingTokens) || packs[packs.length - 1];

  return (
    <div className="grid grid-cols-3 gap-3 sm:grid-cols-6">
      {packs.map((p) => {
        const isRecommended = p === recommendedPack;
        const isLoading = loadingPack === p;
        return (
          <button
            key={p}
            type="button"
            data-testid={CHECKOUT.buyPackBtn(p)}
            onClick={() => onBuy(p)}
            disabled={!!loadingPack}
            className={`relative rounded-2xl border p-4 text-center transition-all hover:border-primary/60 hover:-translate-y-0.5 disabled:opacity-60 ${
              isRecommended ? "border-primary bg-primary/10" : "border-border bg-card"
            }`}
          >
            {isRecommended && !isLoading && (
              <span className="absolute -top-2 left-1/2 flex -translate-x-1/2 items-center gap-1 rounded-full bg-primary px-2 py-0.5 text-[10px] font-semibold text-primary-foreground">
                <Sparkles className="h-2.5 w-2.5" />
              </span>
            )}
            {/* Instant visual reaction the moment this pack is tapped (14/08/2026 -
                "order_939b7c6c609f" incident: PostHog flagged repeated taps here as "dead
                clicks" because nothing on screen reacted while the device-capability check in
                Checkout.jsx ran in the background) - overrides everything else in the button
                so a tap is NEVER visually silent, on any device. */}
            {isLoading ? (
              <div className="flex flex-col items-center justify-center" data-testid={`buy-pack-${p}-loading`}>
                <Loader2 className="mx-auto h-5 w-5 animate-spin text-primary" />
                <p className="mt-2 text-xs font-medium text-muted-foreground">
                  {language === "en" ? "Loading..." : "Chargement..."}
                </p>
              </div>
            ) : (
              <>
                <Coins className="mx-auto h-5 w-5 text-primary" />
                <p className="mt-2 font-heading text-lg font-semibold">{p}</p>
                <p className="text-xs text-muted-foreground">{formatPrice(p, language)}</p>
              </>
            )}
          </button>
        );
      })}
    </div>
  );
}
